import { Link, useParams } from "react-router-dom";

import { ApiError } from "../api/errors";
import { Shell } from "../app/Shell";
import { ErrorNotice, Panel, formatDateTime } from "../components/ui";
import { OrderHistoryPanel } from "./OrderHistoryPanel";
import { OrderLinesPanel } from "./OrderLinesPanel";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { OrderTransitionActions } from "./OrderTransitionActions";
import {
  ORDER_STATUS_MEANING,
  paymentMethodLabel,
} from "./orderStatus";
import { useOrder } from "./useOrders";
import "./orders.css";

/**
 * One order, in the form the shop needs to act on it.
 *
 * Four questions, in the order somebody actually asks them: what is this order
 * and where has it got to, who is it for and where is it going, what is on it
 * and what did it cost, and what has happened to it. The actions sit with the
 * first, because deciding what to do next is what the top of this screen is
 * for.
 *
 * Nothing on this page derives a business fact. The total is the stored one,
 * the line names and prices are the snapshots taken when the order was placed,
 * and which actions exist is the backend's answer for this order right now.
 */
export function OrderDetailPage() {
  const { orderId = "" } = useParams();
  const order = useOrder(orderId);

  if (order.isPending) {
    return (
      <Shell title="Order">
        <div className="empty" role="status">
          Loading order…
        </div>
      </Shell>
    );
  }

  if (order.isError) {
    const gone =
      order.error instanceof ApiError && order.error.kind === "not_found";
    return (
      <Shell title="Order">
        <div className="stack">
          <div className="orders__failure">
            <ErrorNotice error={order.error} />
            {gone ? null : (
              <button
                type="button"
                className="btn"
                disabled={order.isFetching}
                onClick={() => void order.refetch()}
              >
                {order.isFetching ? "Retrying…" : "Retry"}
              </button>
            )}
          </div>
          <Link className="btn" to="/orders">
            Back to Orders
          </Link>
        </div>
      </Shell>
    );
  }

  const data = order.data;

  return (
    <Shell
      title={
        <>
          Order <span className="orders__titleNumber">{data.order_number}</span>
        </>
      }
      actions={
        <Link className="btn btn--subtle btn--sm" to="/orders">
          Back to Orders
        </Link>
      }
    >
      <div className="stack">
        <Panel title="Order">
          <dl className="orders__facts">
            <div>
              <dt>Order number</dt>
              <dd className="mono">{data.order_number}</dd>
            </div>
            <div>
              {/* "Date placed", not "Placed": `placed` is also the name of a
                  lifecycle state, and a screen with a field called Placed
                  beside a badge reading Placed asks an operator to work out
                  which of the two any given "Placed" is. */}
              <dt>Date placed</dt>
              <dd>{formatDateTime(data.created_at)}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                <OrderStatusBadge status={data.status} />
                <span className="orders__statusMeaning">
                  {ORDER_STATUS_MEANING[data.status]}
                </span>
              </dd>
            </div>
            <div>
              <dt>Payment</dt>
              {/* The METHOD, which is real. There is deliberately no payment
                  status here: the backend has no authoritative source for one,
                  and a field that is always empty would read as a payment
                  system this shop does not have. */}
              <dd>{paymentMethodLabel(data.payment_method)}</dd>
            </div>
          </dl>

          <div className="orders__actions">
            <OrderTransitionActions order={data} />
          </div>
        </Panel>

        <Panel title="Customer">
          <dl className="orders__facts">
            <div>
              <dt>Name</dt>
              <dd>{data.customer_name}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>
                <a href={`tel:${data.customer_phone}`}>{data.customer_phone}</a>
              </dd>
            </div>
            <div className="orders__facts--wide">
              <dt>Delivery address</dt>
              <dd>{data.delivery_address}</dd>
            </div>
            {/* Both are optional on the order and are shown only when the
                customer gave one. An empty row headed "Landmark" would look
                like something had failed to load. */}
            {data.delivery_landmark ? (
              <div className="orders__facts--wide">
                <dt>Landmark</dt>
                <dd>{data.delivery_landmark}</dd>
              </div>
            ) : null}
            {data.delivery_notes ? (
              <div className="orders__facts--wide">
                <dt>Notes from the customer</dt>
                <dd>{data.delivery_notes}</dd>
              </div>
            ) : null}
          </dl>
        </Panel>

        <OrderLinesPanel order={data} />

        <OrderHistoryPanel orderId={orderId} />
      </div>
    </Shell>
  );
}
